import type {
  StatementActionResponse,
  StatementFilterOptionsResponse,
  StatementListItem,
  StatementListQueryParams,
  StatementListResponse,
  StatementRegenerateRequest,
} from '@/types/statement.types';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { downloadFilefromBuffer } from '@/lib/utils';
import axiosInstance from './axiosInstance';

const BASE_URL = '/statement';

const toDateString = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return value as string;
};

export const getStatementListData = async (params: StatementListQueryParams): Promise<StatementListResponse> => {
  const response = await axiosInstance.get<StatementListResponse>(BASE_URL, {
    params: {
      ...params,
      clientIds: params.clientIds?.length ? JSON.stringify(params.clientIds) : undefined,
      fileNumbers: params.fileNumbers?.length ? JSON.stringify(params.fileNumbers) : undefined,
      fromDate: toDateString(params.fromDate),
      toDate: toDateString(params.toDate),
    },
  });
  return response.data;
};

const getHeaderValue = (headers: unknown, headerName: string): string | undefined => {
  if (!headers || typeof headers !== 'object') return undefined;

  const headerRecord = headers as Record<string, unknown>;
  const value = headerRecord[headerName] ?? headerRecord[headerName.toLowerCase()];
  return typeof value === 'string' ? value : undefined;
};

const getFileNameFromContentDisposition = (contentDisposition?: string): string | undefined => {
  if (!contentDisposition) return undefined;

  const encodedMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) return decodeURIComponent(encodedMatch[1].replace(/"/g, ''));

  const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return fileNameMatch?.[1];
};

const getExportFileType = (contentType?: string, fileName?: string): 'CSV' | 'PDF' | 'ZIP' => {
  const normalizedContentType = contentType?.toLowerCase() ?? '';
  const normalizedFileName = fileName?.toLowerCase() ?? '';

  if (normalizedContentType.includes('zip') || normalizedFileName.endsWith('.zip')) return 'ZIP';
  if (normalizedContentType.includes('csv') || normalizedFileName.endsWith('.csv')) return 'CSV';
  return 'PDF';
};

export const exportStatements = async (statementIds: string[]): Promise<void> => {
  const response = await axiosInstance.post<Blob>(`${BASE_URL}/export`, { statementIds }, { responseType: 'blob' });
  const contentDisposition = getHeaderValue(response.headers, 'content-disposition');
  const contentType = getHeaderValue(response.headers, 'content-type');
  const parsedFileName = getFileNameFromContentDisposition(contentDisposition);
  const fileType = getExportFileType(contentType, parsedFileName);
  const fileName = parsedFileName ?? `statements_export.${fileType.toLowerCase()}`;
  downloadFilefromBuffer(response.data, fileName, fileType);
};

export const emailStatements = async (statementIds: string[]): Promise<StatementActionResponse> => {
  const response = await axiosInstance.post<StatementActionResponse>(`${BASE_URL}/email`, {
    statementIds,
  });
  return response.data;
};

export const regenerateStatements = async (payload: StatementRegenerateRequest): Promise<StatementActionResponse> => {
  const response = await axiosInstance.post<StatementActionResponse>(`${BASE_URL}/regenerate`, payload);
  return response.data;
};

export const useStatementFilterOptions = (enabled = true) => {
  return useQuery<StatementFilterOptionsResponse>({
    queryKey: ['statement-filter-options'],
    queryFn: () =>
      axiosInstance.get<StatementFilterOptionsResponse>(`${BASE_URL}/filter-options`).then((res) => res.data),
    staleTime: 1000 * 60 * 5,
    enabled,
  });
};

export const useGetStatements = (
  params: StatementListQueryParams,
  options?: { query?: Partial<UseQueryOptions<StatementListResponse>> }
): {
  data: StatementListItem[];
  totalRows: number;
  totalAmountDue?: string;
  lessThan30DaysDue?: string;
  past30DaysDue?: string;
  past60DaysDue?: string;
  past90DaysDue?: string;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
} => {
  const query = useQuery<StatementListResponse, Error>({
    queryFn: () => getStatementListData(params),
    queryKey: ['statements', params],
    ...options?.query,
  });

  const responseData = query.data;

  return {
    data: responseData?.data ?? [],
    totalRows: responseData?.totalRows ?? 0,
    totalAmountDue: responseData?.totalAmountDue,
    lessThan30DaysDue: responseData?.lessThan30DaysDue,
    past30DaysDue: responseData?.past30DaysDue,
    past60DaysDue: responseData?.past60DaysDue,
    past90DaysDue: responseData?.past90DaysDue,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
  };
};
